import { ArrowRight, ChevronDown, ArrowLeft, X, Menu } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// Route-to-chunk map for prefetching on hover
const routePrefetchMap: Record<string, () => Promise<unknown>> = {
  "/services": () => import("@/pages/Services"),
  "/about": () => import("@/pages/AboutUs"),
  "/contact": () => import("@/pages/ContactUs"),
  "/academy": () => import("@/pages/Academy"),
  "/studio": () => import("@/pages/Studio"),
  "/blog": () => import("@/pages/Blog"),
};
const prefetched = new Set<string>();

const Navigation = () => {
  const [hasMounted, setHasMounted] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [highlightedTab, setHighlightedTab] = useState<string>("Home");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [navDark, setNavDark] = useState(false);

  const dropdownRefs = useRef<{[key: string]: HTMLDivElement | null;}>({});
  const tabRefs = useRef<{[key: string]: HTMLElement | null;}>({});
  const location = useLocation();

  const navigationItems = [
  { name: "Home", href: "/", hasDropdown: false },
  { name: "Our Services", href: "/services", hasDropdown: false },
  { name: "Bazaar", href: "/bazaar", hasDropdown: false },
  { name: "About Us", href: "/about", hasDropdown: false },
  { name: "Contact Us", href: "/contact", hasDropdown: false }];


  // Derive the current page tab from the route
  const getCurrentTab = () => {
    const path = location.pathname;
    const match = navigationItems.find((item) =>
    item.href === '/' ? path === '/' : path.startsWith(item.href)
    );
    return match?.name || "Home";
  };

  // Sync highlighted tab with route on navigation
  useEffect(() => {
    setHighlightedTab(getCurrentTab());
  }, [location.pathname]);


  // Mark mounted after first render
  useEffect(() => {
    requestAnimationFrame(() => setHasMounted(true));
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Detect section background behind nav using IntersectionObserver (no forced reflows)
  // Uses MutationObserver to pick up lazily-mounted sections
  useEffect(() => {
    const navHeight = 64;
    const visibleDarkSections = new Set<Element>();
    const observedElements = new WeakSet<Element>();

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (entry.target.getAttribute('data-nav-theme') === 'dark') {
              visibleDarkSections.add(entry.target);
            }
          } else {
            visibleDarkSections.delete(entry.target);
          }
        }
        setNavDark(visibleDarkSections.size > 0);
      },
      {
        rootMargin: `0px 0px -${window.innerHeight - navHeight}px 0px`,
        threshold: 0,
      }
    );

    const observeSections = () => {
      document.querySelectorAll('[data-nav-theme]').forEach((s) => {
        if (!observedElements.has(s)) {
          observedElements.add(s);
          intersectionObserver.observe(s);
        }
      });
    };

    observeSections();

    // Watch for lazily-mounted sections
    const mutationObserver = new MutationObserver(() => observeSections());
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown && dropdownRefs.current[activeDropdown]) {
        const dropdownElement = dropdownRefs.current[activeDropdown];
        if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
          setActiveDropdown(null);
          setActiveSubmenu(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  const handleDropdownToggle = (itemName: string) => {
    setActiveDropdown(activeDropdown === itemName ? null : itemName);
    setActiveSubmenu(null);
  };



  return (
    <nav className={`nav-container ${navDark ? 'nav-theme-dark' : ''}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="logo-hover flex items-center">
              <img
                src="/lovable-uploads/ai-hub-logo.webp"
                alt="AI hub"
                width={96}
                height={96}
                className={`h-24 w-auto transition-all duration-300 hover:opacity-80 ${navDark ? 'brightness-0' : 'brightness-0 invert'}`} />
              
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="hidden lg:block">
            <div className="relative ml-10 flex items-center">
              {/* Pills Background Container */}
              <div className="relative flex items-center rounded-full p-1 transition-all duration-300" onMouseLeave={() => setHighlightedTab(getCurrentTab())} style={navDark ? { background: 'hsla(0, 0%, 0%, 0.06)', backdropFilter: 'blur(12px)', border: '1px solid hsla(0, 0%, 0%, 0.1)', boxShadow: 'inset 0 1px 0 hsla(0, 0%, 0%, 0.04), 0 2px 12px hsla(0, 0%, 0%, 0.05)' } : { background: 'hsla(0, 0%, 100%, 0.1)', backdropFilter: 'blur(12px)', border: '1px solid hsla(0, 0%, 100%, 0.15)', boxShadow: 'inset 0 1px 0 hsla(0, 0%, 100%, 0.1), 0 2px 12px hsla(0, 0%, 0%, 0.1)' }}>
                {navigationItems.map((item) => {
                  const isHighlighted = highlightedTab === item.name;
                  const isCurrentPage = getCurrentTab() === item.name;
                  return (
                    <div
                      key={item.name}
                      className="relative z-10"
                      ref={(el) => {
                        if (el) dropdownRefs.current[item.name] = el;
                      }}
                      onMouseEnter={() => setHighlightedTab(item.name)}>
                      
                      {isHighlighted &&
                      <motion.div
                        layoutId="nav-pill-indicator"
                        className="absolute inset-0 rounded-full"
                        style={{ background: navDark ? 'hsla(0, 0%, 0%, 0.08)' : 'hsla(0, 0%, 100%, 0.2)' }}
                        transition={hasMounted ? {
                          type: "spring",
                          stiffness: 500,
                          damping: 35,
                          mass: 0.8
                        } : { duration: 0 }} />

                      }
                      {item.hasDropdown ?
                      <button
                        ref={(el) => {
                          if (el) tabRefs.current[item.name] = el;
                        }}
                        onClick={() => {
                          setHighlightedTab(item.name);
                          handleDropdownToggle(item.name);
                        }}
                        className={`nav-pill ${isCurrentPage ? 'active' : ''}`}>
                        
                          {item.name}
                          <ChevronDown
                          className={`ml-1 h-4 w-4 transition-transform duration-200 ${
                          activeDropdown === item.name ? 'rotate-180' : ''}`
                          } />
                        
                        </button> :

                      <Link
                        ref={(el) => {
                          if (el) tabRefs.current[item.name] = el;
                        }}
                        to={item.href}
                        onClick={() => setHighlightedTab(item.name)}
                        onMouseEnter={() => {
                          const loader = routePrefetchMap[item.href];
                          if (loader && !prefetched.has(item.href)) {
                            prefetched.add(item.href);
                            loader();
                          }
                        }}
                        className={`nav-pill ${isCurrentPage ? 'active' : ''}`}>
                        
                          {item.name}
                        </Link>
                      }
                    </div>);

                })}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            <a href="https://narva.ai/" target="_blank" rel="noopener noreferrer" className={`relative inline-flex items-center justify-center gap-2 rounded-full pl-5 pr-2 py-2 text-sm font-normal shadow-sm transition-all duration-500 font-general-sans overflow-hidden group hover:shadow-lg ${navDark ? 'text-foreground/80 hover:text-foreground' : 'text-white/90 hover:text-white'}`} style={navDark ? { background: 'hsla(0, 0%, 0%, 0.06)', border: '1px solid hsla(0, 0%, 0%, 0.12)' } : { background: 'hsla(0, 0%, 100%, 0.08)', border: '1px solid hsla(0, 0%, 100%, 0.15)' }}>
              <span className="absolute inset-0 rounded-full bg-[hsl(0,0%,10%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                backgroundImage: 'radial-gradient(circle, hsla(0,0%,40%,0.4) 1px, transparent 1px)',
                backgroundSize: '8px 8px',
              }} />
              <span className="relative z-10 group-hover:text-white transition-colors duration-500">Try Narva</span>
              <span className={`relative z-10 flex items-center justify-center w-7 h-7 rounded-full transition-colors duration-300 group-hover:bg-white/20 ${navDark ? 'bg-black/8' : 'bg-white/10'}`}>
                <ArrowRight className="w-4 h-4 -rotate-45 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white" />
              </span>
            </a>
            <a href="https://calendly.com/aihub_ai/ai-hub-30-mins-call" target="_blank" rel="noopener noreferrer" className="relative inline-flex items-center justify-center gap-2 rounded-full liquid-glass-burgundy pl-5 pr-2 py-2 text-sm font-normal text-white shadow-sm transition-all duration-300 font-general-sans overflow-hidden group">
              <span className="relative z-10">Book A Call</span>
              <span className="relative z-10 flex items-center justify-center w-7 h-7 rounded-full bg-white/20 group-hover:bg-white/30 transition-colors duration-300">
                <ArrowRight className="w-4 h-4 -rotate-45 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
              <span className="absolute inset-0 bg-[hsl(0,0%,10%)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-[1]"></span>
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out z-[2]"></span>
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`inline-flex items-center justify-center rounded-md p-2 ${navDark ? 'text-black' : 'text-white'} hover:bg-nav-hover focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary transition-colors`}
            aria-controls="mobile-menu"
            aria-expanded={isMobileMenuOpen}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}>
              
              {isMobileMenuOpen ?
              <X className="h-6 w-6" /> :

              <Menu className="h-6 w-6" />
              }
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`
          }
          id="mobile-menu">
          
          <div className="space-y-1 px-2 pb-3 pt-2 sm:px-3">
            {navigationItems.map((item) =>
            <div key={item.name}>
                <Link
                to={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block rounded-md px-3 py-2 text-base font-medium hover:bg-nav-hover hover:text-primary transition-colors ${navDark ? 'text-foreground' : 'text-white'}`}>
                
                  {item.name}
                </Link>
              </div>
            )}
            <div className="mt-4 flex flex-col space-y-2">
              <a
                href="https://calendly.com/aihub_ai/ai-hub-30-mins-call" target="_blank" rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                className="relative inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(0,45%,35%)] px-5 py-2 text-sm font-normal text-white shadow-sm transition-all duration-300 font-general-sans overflow-hidden group">
                
                <span className="relative z-10">Book A Call</span>
                <ArrowRight className="relative z-10 w-4 h-4 -rotate-45 transition-all duration-300" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>);

};

export default Navigation;